import React, { useRef, useLayoutEffect, useState } from 'react';
import {
  SafeAreaView,
  ScrollView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Image,
  TextInput,
  Dimensions,
  ActivityIndicator,
} from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';

import { scale, scaleVertically } from '../../../utils/scale.utils';
import { Color } from '../../../styles';
import { addHotelReviewApi } from '../../../services/aws/trip.service';
import { selectTokenData } from '../../../store/selectors/auth.selector';
import { openAlert } from '../../../store/actions/alert';
import { updateHotelReview } from '../../../store/actions/payment';
import { HotelReviewApiActions } from '../../../constants/Enums';

import RangeSlider from '../../RangeSlider';

const CategoriesMap = {
  STAFF_COURTESY: 'StaffCourtesy',
  CLEANLINESS: 'Cleanliness',
  COMFORT: 'Comfort',
  ECO_FRIENDLY: 'EcoFriendly',
  AMENITIES: 'Amenities',
  OVERALL_RATING: 'OverallRating',
};
const {
  STAFF_COURTESY,
  CLEANLINESS,
  COMFORT,
  ECO_FRIENDLY,
  AMENITIES,
  OVERALL_RATING,
} = CategoriesMap;
const { CREATE, UPDATE } = HotelReviewApiActions;
const ImagesMap = {
  [STAFF_COURTESY]: require('../../../assets/img/icons/add_review_icons/staff_courtesy.png'),
  [CLEANLINESS]: require('../../../assets/img/icons/add_review_icons/cleanliness.png'),
  [COMFORT]: require('../../../assets/img/icons/add_review_icons/comfort.png'),
  [ECO_FRIENDLY]: require('../../../assets/img/icons/add_review_icons/eco_friendly.png'),
  [AMENITIES]: require('../../../assets/img/icons/add_review_icons/amenities.png'),
  [OVERALL_RATING]: require('../../../assets/img/icons/add_review_icons/overall_rating.png'),
};
const screenDimensions = Dimensions.get('screen');
const { width: screenWidth } = screenDimensions;
const MAX_LENGTH = 200;

const ScoreCard = (props) => {
  const { title, category, updateScore, initialValue = 0 } = props;
  const slider = useRef(initialValue);
  const [hasError, setHasError] = useState(initialValue <= 0);

  const onChange = (newValue) => {
    slider.current = newValue;
    updateScore(category, newValue);
    setHasError((prev) => {
      const error = newValue <= 0;
      if (error !== prev) {
        return error;
      }
      return prev;
    });
  };

  return (
    <View style={scoreCardStyles.scoreCardContainer}>
      <View style={scoreCardStyles.imageContainer}>
        <View style={scoreCardStyles.imageBox}>
          <Image style={styles.icon} source={ImagesMap[category]} />
        </View>
      </View>
      <View style={scoreCardStyles.scoreContainer}>
        <View style={scoreCardStyles.textContainer}>
          <View style={scoreCardStyles.textWrapper}>
            <Text style={scoreCardStyles.text}>{title} </Text>
            {hasError && (
              <>
                <Text style={[scoreCardStyles.text, scoreCardStyles.requiredAsterisk]}> *</Text>
                <View style={scoreCardStyles.requiredContainer}>
                  <Text style={scoreCardStyles.requiredText}>Required</Text>
                </View>
              </>
            )}
          </View>
        </View>
        <View style={scoreCardStyles.rangeSliderContainer}>
          <RangeSlider
            sliderWidth={screenWidth * 0.67}
            stopCount={10}
            onChange={onChange}
            initialValue={slider.current}
          />
        </View>
      </View>
    </View>
  );
};

const AddReviewModal = ({ navigation, route }) => {
  const routeParams = route.params;
  const dispatch = useDispatch();
  const tokenData = useSelector(selectTokenData);
  const {
    HotelName,
    AddressLine1,
    CheckInDateText,
    CheckOutDateText,
    HotelId,
    TripKey,
    HotelReview,
    IsHotelReviewInDB,
  } = routeParams;
  const [checkInWithoutYear] = CheckInDateText?.split(',');
  const [isLoading, setIsLoading] = useState(false);
  const { StaffCourtesy, Cleanliness, Comfort, EcoFriendly, Amenities, OverallRating, Notes = '' } =
    HotelReview || {};
  const [review, setReview] = useState(Notes);
  const initialState = {
    [STAFF_COURTESY]: StaffCourtesy || 0,
    [CLEANLINESS]: Cleanliness || 0,
    [COMFORT]: Comfort || 0,
    [ECO_FRIENDLY]: EcoFriendly || 0,
    [AMENITIES]: Amenities || 0,
    [OVERALL_RATING]: OverallRating || 0,
  };
  const scoresRef = useRef(initialState);

  const updateScore = (category, value) => {
    scoresRef.current = { ...scoresRef.current, [category]: value };
  };

  const addHotelReview = async () => {
    const { userId, siteKey, companyKey } = tokenData;
    const CombineIds = `${siteKey}#${companyKey}#${userId}`;
    const inputFormat = 'MMMM DD, YYYY';
    const outputFormat = 'MM/DD/YYYY';
    const StartDate = moment(CheckInDateText, inputFormat).format(outputFormat);
    const EndDate = moment(CheckOutDateText, inputFormat).format(outputFormat);
    const bodyRequest = {
      ActionCode: IsHotelReviewInDB ? UPDATE : CREATE,
      HotelId: HotelId.toString(),
      CombineIds,
      ...scoresRef.current,
      Notes: review,
      StartDate,
      EndDate,
      TripKey,
    };
    const serializedObject = JSON.stringify(bodyRequest);
    const message =
      'Oops! Something went wrong while adding the review. Please double-check your internet connection and try again';
    try {
      const response = await addHotelReviewApi(serializedObject);
      if (!response) {
        dispatch(openAlert({ message }));
      }
      return response;
    } catch (error) {
      dispatch(openAlert({ message }));
      console.error(error);
      return null;
    }
  };

  const handleSubmit = async () => {
    const hasAnyError = Object.values(scoresRef.current).some((score) => score === 0);
    if (hasAnyError) {
      return;
    }
    setIsLoading(true);
    const response = await addHotelReview();
    if (response) {
      dispatch(updateHotelReview(response));
      dispatch(
        openAlert({
          message: IsHotelReviewInDB
            ? 'Your Review has been successfully updated. Thank you for your continued feedback!'
            : 'Review successfully added to the hotel. Thank you for your feedback!',
          onConfirmPressed: () => {
            navigation.goBack();
          },
        })
      );
    }
    setIsLoading(false);
  };

  useLayoutEffect(() => {
    navigation.setOptions({
      headerStyle: {
        elevation: 0,
        shadowOpacity: 0,
        borderBottomWidth: 0,
        ...CommonReviewStyles.backGroundPrimaryColor,
      },
      headerTintColor: Color.background,
      headerTitleStyle: {
        color: Color.background,
      },
    });
  }, [navigation]);

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.contentContainer} automaticallyAdjustKeyboardInsets={true}>
        <View style={styles.headerContainer}>
          <View style={styles.header}>
            <Text style={styles.titleText}>{HotelName}</Text>
            <Text style={styles.subtitleText}>{AddressLine1}</Text>
            <Text style={styles.subtitleText}>{`${checkInWithoutYear} - ${CheckOutDateText}`}</Text>
          </View>
          <View style={styles.scoreCardList}>
            <ScoreCard
              title="Staff Courtesy"
              category={STAFF_COURTESY}
              updateScore={updateScore}
              initialValue={initialState[STAFF_COURTESY]}
            />
            <ScoreCard
              title="Cleanliness"
              category={CLEANLINESS}
              updateScore={updateScore}
              initialValue={initialState[CLEANLINESS]}
            />
            <ScoreCard
              title="Comfort"
              category={COMFORT}
              updateScore={updateScore}
              initialValue={initialState[COMFORT]}
            />
            <ScoreCard
              title="Eco-friendly"
              category={ECO_FRIENDLY}
              updateScore={updateScore}
              initialValue={initialState[ECO_FRIENDLY]}
            />
            <ScoreCard
              title="Amenities"
              category={AMENITIES}
              updateScore={updateScore}
              initialValue={initialState[AMENITIES]}
            />
            <ScoreCard
              title="Overall Rating"
              category={OVERALL_RATING}
              updateScore={updateScore}
              initialValue={initialState[OVERALL_RATING]}
            />
          </View>
        </View>
        <View style={styles.reviewContainer}>
          <View style={styles.reviewTextContainer}>
            <Text style={styles.reviewText}>Write a review to share your experience</Text>
            <Text style={styles.remainingCharactersText}>{`${review.length} / ${MAX_LENGTH}`}</Text>
          </View>
          <View style={styles.textAreaContainer}>
            <TextInput
              style={styles.textArea}
              multiline
              numberOfLines={4}
              maxLength={MAX_LENGTH}
              value={review}
              onChangeText={setReview}
              placeholder="Type here..."
            />
          </View>
        </View>
      </ScrollView>
      <View style={styles.submitContainer}>
        <TouchableOpacity
          style={[styles.submitButton, CommonReviewStyles.backGroundPrimaryColor]}
          onPress={handleSubmit}
          disabled={isLoading}
        >
          {isLoading ? (
            <ActivityIndicator color={Color.accent} size="large"></ActivityIndicator>
          ) : (
            <Text style={styles.submitText}>Submit</Text>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const CommonReviewStyles = StyleSheet.create({
  borderTopRadius: {
    borderTopRightRadius: scale(28),
    borderTopLeftRadius: scale(28),
  },
  titleText: {
    fontSize: scale(19),
  },
  paddingHorizontal: {
    paddingHorizontal: scale(9),
  },
  textPrimaryColor: {
    color: Color.addReview.primaryColor,
  },
  backGroundPrimaryColor: {
    backgroundColor: Color.addReview.primaryColor,
  },
});

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'center',
    ...CommonReviewStyles.backGroundPrimaryColor,
  },
  contentContainer: {
    flex: 1,
    width: screenWidth,
    backgroundColor: Color.background,
    ...CommonReviewStyles.borderTopRadius,
  },
  headerContainer: {
    flex: 1,
    alignContent: 'center',
    alignItems: 'center',
    backgroundColor: Color.addReview.background,
    ...CommonReviewStyles.borderTopRadius,
  },
  header: {
    alignItems: 'center',
    margin: scaleVertically(8),
    backgroundColor: Color.addReview.background,
  },
  titleText: {
    color: Color.black,
    fontWeight: 'bold',
    ...CommonReviewStyles.titleText,
  },
  subtitleText: {
    color: Color.addReview.subtitle,
    ...CommonReviewStyles.titleText,
  },
  scoreCardList: {
    flex: 1,
    justifyContent: 'space-around',
    width: '100%',
    backgroundColor: Color.background,
  },
  reviewContainer: {
    backgroundColor: Color.background,
    width: screenWidth,
  },
  reviewTextContainer: {
    width: screenWidth,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    ...CommonReviewStyles.paddingHorizontal,
    paddingTop: scaleVertically(4),
  },
  reviewText: {
    fontSize: scaleVertically(12),
    fontWeight: 'bold',
  },
  remainingCharactersText: {
    fontSize: scaleVertically(9),
    lineHeight: scaleVertically(9) + 2,
    color: Color.addReview.remainingChars,
  },
  textAreaContainer: {
    ...CommonReviewStyles.paddingHorizontal,
    paddingBottom: scaleVertically(4),
  },
  textArea: {
    borderWidth: scale(1),
    borderColor: Color.addReview.textAreaBorder,
    borderRadius: scale(7),
    padding: scale(2),
  },
  submitContainer: {
    width: screenWidth,
    height: '8%',
  },
  submitButton: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  submitText: {
    color: Color.background,
    fontSize: scaleVertically(20),
  },
  icon: {
    width: scale(40),
    height: scale(40),
  },
});

const scoreCardStyles = StyleSheet.create({
  scoreCardContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    height: scaleVertically(60),
    alignItems: 'center',
    marginVertical: scaleVertically(3.5),
  },
  imageContainer: {
    width: '20%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    padding: scale(9),
  },
  imageBox: {
    backgroundColor: Color.addReview.background,
    flex: 1,
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: scale(8),
  },
  scoreContainer: {
    width: '80%',
    height: '100%',
    justifyContent: 'center',
    ...CommonReviewStyles.paddingHorizontal,
  },
  textContainer: {
    height: '35%',
    width: '100%',
    justifyContent: 'flex-end',
  },
  textWrapper: {
    flexDirection: 'row',
  },
  text: {
    fontSize: scaleVertically(12),
    lineHeight: scaleVertically(12) + 2,
    fontWeight: 'bold',
  },
  requiredContainer: { justifyContent: 'flex-end' },
  requiredAsterisk: {
    ...CommonReviewStyles.textPrimaryColor,
  },
  requiredText: {
    fontSize: scaleVertically(9),
    lineHeight: scaleVertically(9) + 2,
    fontWeight: 'bold',
    ...CommonReviewStyles.textPrimaryColor,
  },
  rangeSliderContainer: {
    flex: 1,
    width: '100%',
    paddingTop: scaleVertically(10),
  },
});

export default AddReviewModal;
